// The Payables and Receivables bars on Home > Overview, as the ECI mobile app draws them: each
// balance card's bar and legend, the two bars on one $ scale, and the card link's spoken label.
// No Angular here, so Node can test it (balanceBarView.test.mjs). MemberHomeCtrl builds the
// splits and labels with balanceSplits and balanceCardLabel; the balance-bar directive draws what
// balanceBarView returns.

import {barSegments, payableAging, receivableAging, unsplit} from './balanceAging.js';

/** @typedef {import('./balanceAging.js').AgingSplit} AgingSplit */

/**
 * @typedef {Object} BarSegment
 * @property {number} x
 * @property {number} width
 * @property {string} cls Its colour class.
 */

/**
 * What the balance-bar template binds.
 * @typedef {Object} BalanceBarView
 * @property {{width: number, height: number, rects: BarSegment[], end: BarSegment & {path: string}} | null} bar
 *   The SVG's size (the whole track), every drawn segment but the last as a rect, and the last
 *   one, with its right end rounded, as a path. Null when no bar is drawn.
 * @property {{cls: string, text: string}[] | null} legend Null when there is no legend.
 */

/**
 * The two cards, by balance-bar's `kind`: the web's card title, then the legend labels and colour
 * classes in bar order, the part that needs attention first and the rest second.
 */
export const KINDS = {
  payables: {
    title: 'Payables',
    labels: ['Late (14+ days)', 'Current'],
    classes: ['hc-late', 'hc-current'],
  },
  receivables: {
    title: 'Receivables',
    labels: ['Waiting on paperwork', 'Ready'],
    classes: ['hc-waiting', 'hc-ready'],
  },
};

const BAR_HEIGHT = 12;
const RADIUS = 4;

/** Money in the new labels, on magnitudes: 1234.5 gives "$1,234.50". */
export function formatMoney(n) {
  return '$' + n.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2});
}

/**
 * Both cards' splits, and the scale they share: the larger total fills the track.
 *
 * paymentDue and needs are the lists, or null while they load and after a failure. A null list
 * is unknown, not empty: its bar is drawn undivided and without a legend, so nothing is claimed
 * late or waiting. The app does this for paymentDue only: it loads needs together with the
 * balances and retries them all, so it never draws with a failed needs list. The web loads each
 * list on its own, so either can fail.
 *
 * @param {{BuyerBal: number, SellerBal: number}} balances
 * @param {{BalBuyer: number}[] | null} paymentDue
 * @param {{BalSeller: number}[] | null} needs
 * @returns {{payable: AgingSplit, receivable: AgingSplit, scaleMax: number}}
 */
export function balanceSplits(balances, paymentDue, needs) {
  const payable = payableAging(balances.BuyerBal, paymentDue);
  const receivable = needs ? receivableAging(balances.SellerBal, needs) : unsplit(balances.SellerBal, 1);
  return {payable, receivable, scaleMax: Math.max(payable.total, receivable.total)};
}

/**
 * The card link's aria-label, which speaks for the whole card, bar and legend included:
 * "Payables $1,000.00: Late (14+ days) $500.00, Current $500.00". An unknown split leaves the
 * parts out. With nothing owed, a net credit, or before the split has settled (null), it reads
 * the total as the card shows it ("-$1,234.00").
 *
 * @param {'payables'|'receivables'} kind
 * @param {AgingSplit|null} split
 * @param {string} shownTotal The card's total text, from the currency filter.
 * @returns {string}
 */
export function balanceCardLabel(kind, split, shownTotal) {
  const {title, labels} = KINDS[kind];
  if (!(split && split.total > 0)) {
    return (title + ' ' + (shownTotal || '')).trim();
  }
  if (split.unknown) {
    return `${title} ${formatMoney(split.total)}`;
  }
  return `${title} ${formatMoney(split.total)}: ${labels[0]} ${formatMoney(split.part)}, ` +
    `${labels[1]} ${formatMoney(split.rest)}`;
}

/** A segment with only its right end rounded: the bar's last drawn segment. */
export function roundedEnd(x, y, w, h) {
  const r = Math.min(RADIUS, h / 2, w);
  return `M${x} ${y}H${x + w - r}Q${x + w} ${y} ${x + w} ${y + r}V${y + h - r}Q${x + w} ${y + h} ${x + w - r} ${y + h}H${x}Z`;
}

/**
 * One card's bar and legend.
 *
 * - No bar and no legend when nothing is owed or the balance is a net credit (total 0): the card
 *   keeps its total.
 * - The bar's values are [part, rest], placed by barSegments on the shared scale. An unknown
 *   split draws [0, total], so the whole bar is in the second colour, and has no legend.
 * - `width` is the track: the directive's content width, undefined until it is first measured.
 *   Until then there's nothing to place, so no bar.
 * - Zero-width segments aren't drawn. Only the last drawn one is rounded, at its right end. No
 *   outlines.
 * - The legend shows both items, even at $0.00.
 *
 * @param {AgingSplit|null|undefined} split
 * @param {number} scaleMax The larger of the two cards' totals.
 * @param {number|undefined} width
 * @param {'payables'|'receivables'} kind
 * @returns {BalanceBarView}
 */
export function balanceBarView(split, scaleMax, width, kind) {
  const {labels, classes} = KINDS[kind];
  if (!(split && split.total > 0)) {
    return {bar: null, legend: null};
  }
  const track = width > 0 ? width : 0;
  const values = split.unknown ? [0, split.total] : [split.part, split.rest];
  const drawn = barSegments(values, scaleMax, track)
    .map((s, i) => ({x: s.x, width: s.width, cls: classes[i]}))
    .filter(s => s.width > 0);
  const end = drawn.pop();
  return {
    bar: end ? {
      width: track,
      height: BAR_HEIGHT,
      rects: drawn,
      end: {...end, path: roundedEnd(end.x, 0, end.width, BAR_HEIGHT)},
    } : null,
    legend: split.unknown ? null : labels.map((label, i) => ({
      cls: classes[i],
      text: `${label} ${formatMoney(i === 0 ? split.part : split.rest)}`,
    })),
  };
}
